'use client';

import { isPlainPaginationClick, paginationHref } from '@eliadmin/core/src/utils/pagination-link';

import type { PaginationLinks, PaginationMeta } from '@eliadmin/core/src/api/api-types';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import {
  isGlobalNavigation,
  startGlobalNavigation,
  useGlobalNavigation,
} from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

export type PaginationProps = {
  links: Required<PaginationMeta['links']>;
  className?: {
    main?: string;
    item?: string;
    replaceItem?: string;
  };
  pageKey?: string;
  shouldReplace?: boolean;
  setPage?: (page: number) => void;
  onPageClicked?: (page: number) => void;
  scrollToElementAfterPageChange?: string;
  resetWhen?: unknown[];
};

export function Pagination({ pageKey = 'page', ...props }: PaginationProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isNavigating = useGlobalNavigation();
  const [clicked, setClicked] = useState<number | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const didInitializeResetWhen = useRef(false);
  const breakpoint = useBreakpoint('sm');

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTo({ top: 0, behavior: 'smooth' });

    const mainElement = document.querySelector('main');
    if (mainElement instanceof HTMLElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const resolvePageFromUrl = (url?: string) => {
    if (!url) return undefined;

    try {
      const urlObject = new URL(
        url,
        typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
      );
      const page = urlObject.searchParams.get('page');
      const parsedPage = page ? Number(page) : undefined;

      return parsedPage && !Number.isNaN(parsedPage) ? parsedPage : undefined;
    } catch {
      return undefined;
    }
  };

  const getUrl = (url?: string) => {
    if (!url) return undefined;
    try {
      const page = Number(new URL(url, 'http://localhost').searchParams.get('page'));
      return paginationHref(pathname, searchParams, page, pageKey);
    } catch {
      return undefined;
    }
  };

  const getPageFromLink = (link: PaginationLinks) => {
    const pageFromUrl = resolvePageFromUrl(link.url ?? undefined);
    if (pageFromUrl) return pageFromUrl;

    const parsedLabel = Number(link.label);
    return Number.isNaN(parsedLabel) ? undefined : parsedLabel;
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (clicked !== null && !isNavigating) {
      setClicked(null);
      if (
        !props.scrollToElementAfterPageChange ||
        props.scrollToElementAfterPageChange === '__top__'
      ) {
        scrollToTop();
        return;
      }

      const targetElement = document.getElementById(props.scrollToElementAfterPageChange);
      targetElement?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clicked, isNavigating]);

  const shouldShowPaginationItemMobile = (currentItemIndex: number, linksLength: number) => {
    if (!props.links) return true;
    const activeIndex = props.links.findIndex(link => link.active);

    if (currentItemIndex === 0 || currentItemIndex === linksLength - 1) return true;

    let minIndexToShow;
    let maxIndexToShow;

    if (activeIndex <= 2) {
      minIndexToShow = 1;
      maxIndexToShow = 5;
    } else if (activeIndex >= linksLength - 4) {
      minIndexToShow = linksLength - 6;
      maxIndexToShow = linksLength - 2;
    } else {
      minIndexToShow = activeIndex - 2;
      maxIndexToShow = activeIndex + 2;
    }

    return currentItemIndex >= minIndexToShow && currentItemIndex <= maxIndexToShow;
  };

  useEffect(() => {
    if (!props.resetWhen) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.resetWhen)]);

  useEffect(() => {
    if (!props.resetWhen) return;
    if (!didInitializeResetWhen.current) {
      didInitializeResetWhen.current = true;
      return;
    }

    if (props.shouldReplace && props.setPage) {
      props.setPage(1);
      props.onPageClicked?.(1);
      setClicked(null);
      return;
    }

    const currentSearchParams = new URLSearchParams(searchParams);
    currentSearchParams.set(pageKey, '1');
    window.history.replaceState({}, '', `${pathname}?${currentSearchParams.toString()}`);
    props.onPageClicked?.(1);
    setClicked(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.resetWhen)]);

  return (
    <div
      className={cn('flex flex-row items-center space-x-2', 'ea-pagination', props.className?.main)}
    >
      {props.links?.map((link, i) => {
        const href = getUrl(link.url ?? undefined);
        const targetPage = getPageFromLink(link);
        const disabled = !href;
        const pending = clicked !== null || isNavigating;

        if (
          props.links?.length &&
          isMounted &&
          !breakpoint &&
          !shouldShowPaginationItemMobile(i, props.links.length)
        ) {
          return null;
        }

        const content =
          i === 0 ? (
            <LuChevronLeft className="h-4 w-4" />
          ) : i === props.links!.length - 1 ? (
            <LuChevronRight className="h-4 w-4" />
          ) : (
            link.label
          );

        return (
          <Button
            key={`${link.label}-${link.url}-${i}`}
            className={cn('col-span-1 h-9 w-9 p-1', 'ea-pagination__item', props.className?.item)}
            asChild={!disabled && !pending}
            type={disabled || pending ? 'button' : undefined}
            disabled={disabled || pending}
            variant={link.active ? 'default' : disabled ? 'ghost' : 'secondary'}
            onClick={event => {
              if (disabled || pending) {
                event.preventDefault();
                return;
              }
              if (!isPlainPaginationClick(event)) return;
              if (props.shouldReplace && props.setPage) event.preventDefault();
              if (!targetPage) return;

              if ((searchParams.get(pageKey) ?? '1') === String(targetPage)) {
                event.preventDefault();
                props.onPageClicked?.(targetPage);
                return;
              }

              if (!props.scrollToElementAfterPageChange) {
                scrollToTop();
              }

              if (!isGlobalNavigation() && !props.shouldReplace) {
                startGlobalNavigation();
                setClicked(i);
                props.onPageClicked?.(targetPage);
                return;
              }

              if (props.shouldReplace && props.setPage) {
                props.setPage(targetPage);
                setClicked(i);
                props.onPageClicked?.(targetPage);
              }
            }}
            isLoading={clicked === i}
          >
            {disabled || pending ? (
              content
            ) : !props.shouldReplace || !props.setPage ? (
              <Link
                prefetchOnHover
                scroll={!props.scrollToElementAfterPageChange}
                href={href ?? '#'}
              >
                {content}
              </Link>
            ) : (
              <Link
                href={href!}
                prefetch={false}
                prefetchOnHover={false}
                className={cn(
                  'ea-pagination__item-replace-custom cursor-pointer',
                  disabled && 'cursor-default',
                  link.active && 'bg-primary text-background',
                  props.className?.replaceItem,
                )}
              >
                {content}
              </Link>
            )}
          </Button>
        );
      })}
    </div>
  );
}
